import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Box, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip,
  CircularProgress, Alert, TextField, InputAdornment,
  Button, Tooltip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, MenuItem, Snackbar,
  Autocomplete, Radio, RadioGroup, FormControlLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useNavigate } from 'react-router-dom';
import ModuleHeader from '../components/ModuleHeader';
import api from '../axios';

const ESTADO_CIVIL = ['Solteiro(a)', 'Casado(a)', 'Divorciado(a)', 'Viuvo(a)', 'Uniao estavel'];
const MEIO_INGRESSO = ['Batismo', 'Transferencia'];
const STATUS_MEMBRESIA = ['Ativo', 'Inativo', 'Transferido', 'Falecimento', 'Doenca'];

const FORM_EMPTY = {
  nome: '', email: '', telefone: '', estado_civil: '',
  endereco: '', bairro: '', cep: '', data_nascimento: '',
  igreja_origem: '', data_ingresso_lagoinha: '', meio_ingresso: '',
  status_membresia: '', observacoes: '',
  data_primeira_visita: '', data_decisao: '', data_batismo: '',
  conjuge_nome: '', conjuge_membro_id: null, fez_start: '',
};

const ORIGEM_LABEL = { migracao: 'Start', excel: 'Excel', manual: 'Manual' };

export default function MembrosSecretaria() {
  const navigate = useNavigate();
  const [membros, setMembros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [busca, setBusca] = useState('');
  const [snack, setSnack] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_EMPTY);
  const [formErro, setFormErro] = useState('');
  const [saving, setSaving] = useState(false);

  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchMembros = () => {
    setLoading(true);
    api.get('/secretaria/membros')
      .then(r => setMembros(r.data))
      .catch(() => setErro('Nao foi possivel carregar os membros.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchMembros(); }, []);

  const filtrados = membros.filter(m =>
    (m.nome || '').toLowerCase().includes(busca.toLowerCase()) ||
    (m.email || '').toLowerCase().includes(busca.toLowerCase()) ||
    (m.telefone || '').includes(busca)
  );

  const openCreate = () => {
    setEditando(null);
    setForm(FORM_EMPTY);
    setFormErro('');
    setDialogOpen(true);
  };

  const openEdit = (m) => {
    setEditando(m);
    setForm({
      nome: m.nome || '',
      email: m.email || '',
      telefone: m.telefone || '',
      estado_civil: m.estado_civil || '',
      endereco: m.endereco || '',
      bairro: m.bairro || '',
      cep: m.cep || '',
      data_nascimento: m.data_nascimento || '',
      igreja_origem: m.igreja_origem || '',
      data_ingresso_lagoinha: m.data_ingresso_lagoinha || '',
      meio_ingresso: m.meio_ingresso || '',
      status_membresia: m.status_membresia || '',
      observacoes: m.observacoes || '',
      data_primeira_visita: m.data_primeira_visita || '',
      data_decisao: m.data_decisao || '',
      data_batismo: m.data_batismo || '',
      conjuge_nome: m.conjuge_nome || '',
      conjuge_membro_id: m.conjuge_membro_id || null,
      fez_start: m.fez_start || '',
    });
    setFormErro('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.nome.trim()) { setFormErro('Nome e obrigatorio.'); return; }
    setSaving(true);
    setFormErro('');
    try {
      if (editando) {
        await api.put('/secretaria/membros/' + editando.id, form);
        setSnack('Membro atualizado com sucesso!');
      } else {
        await api.post('/secretaria/membros', form);
        setSnack('Membro cadastrado com sucesso!');
      }
      setDialogOpen(false);
      fetchMembros();
    } catch (e) {
      setFormErro((e && e.response && e.response.data && e.response.data.detail) || 'Erro ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await api.delete('/secretaria/membros/' + deleteId);
      setSnack('Membro removido.');
      setDeleteId(null);
      fetchMembros();
    } catch {
      setSnack('Erro ao remover membro.');
      setDeleteId(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
        <ModuleHeader module="Secretaria" title="Membros da Secretaria" onBack={() => navigate('/secretaria')} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={openCreate}
          >
            Novo membro
          </Button>
        </Box>

        <TextField
          placeholder="Buscar por nome, e-mail ou telefone..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
          fullWidth
          size="small"
          sx={{ mb: 3 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
        />

        {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : filtrados.length === 0 ? (
          <Alert severity="info">
            {busca ? 'Nenhum membro encontrado para esta busca.' : 'Nenhum membro cadastrado ainda.'}
          </Alert>
        ) : (
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Nome</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>E-mail</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Telefone</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Estado Civil</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Nascimento</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Origem</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', width: 60 }}>Jornada</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', width: 90 }}>Acoes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtrados.map(m => (
                  <TableRow key={m.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{m.nome || '\u2014'}</TableCell>
                    <TableCell>{m.email || '\u2014'}</TableCell>
                    <TableCell>{m.telefone || '\u2014'}</TableCell>
                    <TableCell>{m.estado_civil || '\u2014'}</TableCell>
                    <TableCell>{m.data_nascimento || '\u2014'}</TableCell>
                    <TableCell>
                      <Chip
                        label={m.status_membresia || '\u2014'}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ORIGEM_LABEL[m.origem] || m.origem || '\u2014'}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Ver Jornada">
                        <IconButton size="small" color="primary" onClick={() => navigate(`/jornada/membro/${m.id}`)}>
                          <TimelineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Editar">
                        <IconButton size="small" color="primary" onClick={() => openEdit(m)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" color="error" onClick={() => setDeleteId(m.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>{editando ? 'Editar membro' : 'Novo membro'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                label="Nome *"
                value={form.nome}
                onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="E-mail"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                fullWidth
                size="small"
                type="email"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Telefone"
                value={form.telefone}
                onChange={e => setForm(f => ({ ...f, telefone: e.target.value }))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Estado Civil"
                value={form.estado_civil}
                onChange={e => setForm(f => ({ ...f, estado_civil: e.target.value }))}
                fullWidth
                size="small"
              >
                <MenuItem value="">-</MenuItem>
                {ESTADO_CIVIL.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Data de nascimento"
                value={form.data_nascimento}
                onChange={e => setForm(f => ({ ...f, data_nascimento: e.target.value }))}
                fullWidth
                size="small"
                placeholder="dd/mm/aaaa"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Endereco"
                value={form.endereco}
                onChange={e => setForm(f => ({ ...f, endereco: e.target.value }))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Bairro"
                value={form.bairro}
                onChange={e => setForm(f => ({ ...f, bairro: e.target.value }))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="CEP"
                value={form.cep}
                onChange={e => setForm(f => ({ ...f, cep: e.target.value }))}
                fullWidth
                size="small"
                placeholder="00000-000"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Igreja de Origem"
                value={form.igreja_origem}
                onChange={e => setForm(f => ({ ...f, igreja_origem: e.target.value }))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Data de Ingresso na Lagoinha"
                value={form.data_ingresso_lagoinha}
                onChange={e => setForm(f => ({ ...f, data_ingresso_lagoinha: e.target.value }))}
                fullWidth
                size="small"
                placeholder="dd/mm/aaaa"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Meio de Ingresso"
                value={form.meio_ingresso}
                onChange={e => setForm(f => ({ ...f, meio_ingresso: e.target.value }))}
                fullWidth
                size="small"
              >
                <MenuItem value="">-</MenuItem>
                {MEIO_INGRESSO.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Status Membresia"
                value={form.status_membresia}
                onChange={e => setForm(f => ({ ...f, status_membresia: e.target.value }))}
                fullWidth
                size="small"
              >
                <MenuItem value="">-</MenuItem>
                {STATUS_MEMBRESIA.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Data Primeira Visita"
                value={form.data_primeira_visita}
                onChange={e => setForm(f => ({ ...f, data_primeira_visita: e.target.value }))}
                fullWidth
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Data Decisao"
                value={form.data_decisao}
                onChange={e => setForm(f => ({ ...f, data_decisao: e.target.value }))}
                fullWidth
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Data Batismo"
                value={form.data_batismo}
                onChange={e => setForm(f => ({ ...f, data_batismo: e.target.value }))}
                fullWidth
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>Fez o Start?</Typography>
              <RadioGroup
                row
                value={form.fez_start}
                onChange={e => setForm(f => ({ ...f, fez_start: e.target.value }))}
              >
                <FormControlLabel value="Sim" control={<Radio size="small" />} label="SIM" />
                <FormControlLabel value="Nao" control={<Radio size="small" />} label="NAO" />
              </RadioGroup>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={membros.filter(m2 => m2.id !== (editando ? editando.id : -1))}
                getOptionLabel={(m2) => m2.nome || ''}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                value={form.conjuge_membro_id
                  ? membros.find(m2 => m2.id === form.conjuge_membro_id) || null
                  : (form.conjuge_nome ? { id: null, nome: form.conjuge_nome } : null)}
                onChange={(e, nova) => setForm(f => ({
                  ...f,
                  conjuge_nome: nova ? nova.nome : '',
                  conjuge_membro_id: nova && nova.id ? nova.id : null,
                }))}
                renderInput={(params) => (
                  <TextField {...params} label="Conjuge (membro da igreja)" size="small" />
                )}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Observacoes"
                value={form.observacoes}
                onChange={e => setForm(f => ({ ...f, observacoes: e.target.value }))}
                fullWidth
                size="small"
                multiline
                rows={3}
              />
            </Grid>
            {formErro && (
              <Grid item xs={12}>
                <Alert severity="error">{formErro}</Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancelar</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} fullWidth maxWidth="sm">
        <DialogTitle>Confirmar exclusao</DialogTitle>
        <DialogContent>
          <Typography>Tem certeza que deseja remover este membro? Esta acao nao pode ser desfeita.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)} disabled={deleting}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Removendo...' : 'Remover'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={3500}
        onClose={() => setSnack('')}
        message={snack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}
